import * as React from 'react';
import Tabs from '@amzn/awsui-components-console/tabs';
import Alert from '@amzn/awsui-components-console/alert';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Box from '@amzn/awsui-components-console/box';
import ExpandableSection from '@amzn/awsui-components-console/expandable-section';
import PageLayoutWrapper from '../../components/PageLayoutWrapper/PageLayoutWrapper';
import CollectionsTable from './components/CollectionsTable';
import HistoryTab from './components/HistoryTab';
import './DataSourcesPage.scss';

const DataSourcesPage: React.FC = () => {
  const [activeTabId, setActiveTabId] = React.useState('current');
  const [showGuidance, setShowGuidance] = React.useState(true);

  return (
    <PageLayoutWrapper
      heading="Collections"
      breadcrumbItems={[
        { text: 'Home', href: '/' },
        { text: 'Collections', href: '/collections' },
      ]}
    >
      <SpaceBetween size="l">
        {showGuidance && (
          <Alert type="info" dismissible header="🚀 Welcome to Content Collections" onDismiss={() => setShowGuidance(false)}>
            <SpaceBetween size="m">
              <Box>
                <p>
                  A{' '}
                  <Box variant="strong" display="inline">
                    content collection
                  </Box>{' '}
                  is a configured content group that defines what content to ingest, how often to sync it, and where it comes from.
                  Collections are the primary unit of content organization in the system.
                </p>

                <p>
                  Collections automatically sync content based on your configured schedule (hourly, daily, weekly, or monthly), enabling
                  applications to access up-to-date content without manual updates. Simply provide a URL, and we'll handle the rest.
                </p>
              </Box>

              <ExpandableSection headerText="📋 Supported Content Sources" variant="footer">
                <SpaceBetween size="s">
                  <Box>
                    🌐{' '}
                    <Box variant="strong" display="inline">
                      Web
                    </Box>{' '}
                    - Websites and web pages (
                    <Box variant="code" display="inline">
                      https://example.com
                    </Box>
                    )
                  </Box>
                  <Box>
                    📝{' '}
                    <Box variant="strong" display="inline">
                      Quip
                    </Box>{' '}
                    - Documents and folders (
                    <Box variant="code" display="inline">
                      https://quip-amazon.com/...
                    </Box>
                    )
                  </Box>
                  <Box>
                    📚{' '}
                    <Box variant="strong" display="inline">
                      Wiki
                    </Box>{' '}
                    - Internal wiki pages (
                    <Box variant="code" display="inline">
                      https://w.amazon.com/...
                    </Box>
                    )
                  </Box>
                  <Box>
                    💼{' '}
                    <Box variant="strong" display="inline">
                      Highspot
                    </Box>{' '}
                    - Sales enablement content (
                    <Box variant="code" display="inline">
                      https://aws.highspot.com/...
                    </Box>
                    )
                  </Box>
                  <Box>
                    📁{' '}
                    <Box variant="strong" display="inline">
                      BYOC
                    </Box>{' '}
                    - Your own S3 files (
                    <Box variant="code" display="inline">
                      s3://bucket/path
                    </Box>
                    )
                  </Box>
                </SpaceBetween>
              </ExpandableSection>

              <Box>
                <Box variant="strong">✨ Quick Start:</Box> Click "Create collection" below and paste any URL from the supported sources
                above.
              </Box>
            </SpaceBetween>
          </Alert>
        )}

        <Tabs
          activeTabId={activeTabId}
          onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
          tabs={[
            {
              id: 'current',
              label: 'Current',
              content: <CollectionsTable />,
            },
            {
              id: 'history',
              label: 'History',
              content: <HistoryTab />,
            },
          ]}
        />
      </SpaceBetween>
    </PageLayoutWrapper>
  );
};

export default DataSourcesPage;
